/**
 * Chrome — [T149-PR8-CONT-11] outer Card wrapper.
 *
 * Sole owner of:
 *   - The outer `<Card>` element (`role="region"`, `aria-labelledby`)
 *   - `data-testid="widget-frame"` (e2e hook)
 *   - The `glass-panel` toggle (suppressed when `noGlass === true`)
 *   - The `h-full flex flex-col overflow-hidden` flexbox shell
 *
 * Children slots are rendered as-is — Chrome does NOT inspect or
 * transform the slot contents.
 *
 * @see https://www.radix-ui.com/primitives/docs/primitives — slot pattern
 */

import type { ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { ChromeState } from './types';

export interface ChromeProps extends ChromeState {
  headerId: string;
  children: ReactNode;
}

/**
 * Outer Card shell. The `aria-labelledby={headerId}` wires the title
 * element (rendered in `HeaderSlot`) to the region for screen readers.
 */
export function Chrome({
  id: _id,
  className,
  noGlass,
  ariaProps,
  headerId,
  children,
}: ChromeProps) {
  return (
    <Card
      role="region"
      aria-labelledby={headerId}
      data-testid="widget-frame"
      {...ariaProps}
      className={cn(
        'h-full flex flex-col overflow-hidden',
        !noGlass &&
          'glass-panel transition-all duration-200 hover:shadow-md hover:border-primary/20',
        className,
      )}
    >
      {children}
    </Card>
  );
}
